import { getPrice, getDeoMonthly } from "../lib/pricing";
import { useIsMobile } from "../lib/useIsMobile";

// ════════════════════════════════════════════════════════════════════
//  DashboardApple — the apple-native LAYOUT for the dashboard.
//  This is a genuine information redesign, not a reskin: it establishes a
//  hierarchy (operational + money hero up top), surfaces what needs ACTION,
//  promotes revenue, demotes vanity metrics to a quiet strip, and cuts the
//  jobs-today info that used to appear three times.
//
//  PURELY PRESENTATIONAL. It receives the exact same computed data + handlers
//  Dashboard.js already had — no logic, no data fetching, no functional change.
//  It only renders when ACTIVE_THEME === "apple-native"; under "default" the
//  original Dashboard markup is used unchanged. Safe to use apple-native CSS
//  vars (var(--accent) etc.) since this view only mounts under that theme.
// ════════════════════════════════════════════════════════════════════

const clientMonthly = (c, pricing) =>
  getPrice(c.frequency, c.dogs, pricing) +
  (c.deo_enabled && c.deo_status === "active" ? getDeoMonthly(c.deo_frequency, pricing) : 0);

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const money2 = (n) => `$${Number(n || 0).toFixed(2)}`;

export function DashboardApple(props) {
  const {
    clients, todayJobs, jobsDone, toggleJob, setPage, mrr, pricing,
    active, paid, pendingAmt, needsApproval, doneCount,
    avgRevenuePerClient, nextMonthForecast, deactivatedThisMonth, revData,
    org, serviceArea, onSetup,
  } = props;
  const isMobile = useIsMobile();

  // First-run setup: a fresh operator can't add a paying client until Stripe is
  // connected. Surface the remaining steps up top instead of a dead-end empty state.
  const setupSteps = onSetup && org ? [
    { done: !!org.stripe_account_id, label: "Connect payments (Stripe)", desc: "Required to add clients & get paid", go: () => onSetup("business", "plan") },
    { done: !!(pricing?.monthly && Object.keys(pricing.monthly).length > 0), label: "Set your rates", desc: "Your client price list", go: () => onSetup("billing") },
    { done: (serviceArea || []).length > 0, label: "Add your service areas", desc: "Which ZIPs you service, and on which day", go: () => onSetup("team", "area") },
  ] : [];
  const showSetup = !!(onSetup && org && !org.stripe_account_id);

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const dateStr = now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  // Revenue story
  const thisMonthRev = revData[5] || 0;
  const lastMonthRev = revData[4] || 0;
  const momDelta = thisMonthRev - lastMonthRev;
  const momPct = lastMonthRev > 0 ? (momDelta / lastMonthRev) * 100 : null;
  const sixMoTotal = revData.reduce((a, b) => a + b, 0);
  const maxRev = Math.max(...revData, 1);

  // Today
  const total = todayJobs.length;
  const pct = total ? Math.round((doneCount / total) * 100) : 0;
  // Hero completion ring geometry — 96px beside the headline on phones, 128px on desktop.
  const ringSize = isMobile ? 96 : 128;
  const ringR = isMobile ? 39 : 52;
  const ringStroke = isMobile ? 9 : 11;
  const ringC = 2 * Math.PI * ringR;
  const JobRow = isMobile ? "label" : "div";

  // What needs the operator's attention right now (only non-zero items)
  const attention = [
    needsApproval > 0 && { dot: "var(--accent)", text: `${needsApproval} invoice${needsApproval > 1 ? "s" : ""} awaiting approval`, go: () => setPage("invoices") },
    pendingAmt > 0 && { dot: "#E8A33D", text: `${money2(pendingAmt)} in pending payments`, go: () => setPage("invoices") },
    deactivatedThisMonth > 0 && { dot: "var(--danger)", text: `${deactivatedThisMonth} client${deactivatedThisMonth > 1 ? "s" : ""} churned this month`, go: () => setPage("clients") },
  ].filter(Boolean);

  const topClients = clients.filter(c => c.active)
    .sort((a, b) => clientMonthly(b, pricing) - clientMonthly(a, pricing)).slice(0, 6);

  const label = { fontSize: 12, fontWeight: 600, letterSpacing: ".02em", textTransform: "uppercase", color: "var(--text2)" };
  const sub = { fontSize: 13, color: "var(--text2)" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, maxWidth: 1160 }}>

      {/* ── FIRST-RUN SETUP — shown until Stripe is connected (the gate on adding clients) ── */}
      {showSetup && (
        <div style={{ borderRadius: 20, border: "1px solid #F6D88A", background: "#FEF6E7", padding: isMobile ? "16px 15px" : "20px 24px" }}>
          <div style={{ fontSize: 17, fontWeight: 650, color: "#8A5A06", letterSpacing: "-.01em" }}>Finish setup to start billing clients</div>
          <div style={{ fontSize: 13.5, color: "#8A5A06", opacity: .85, marginTop: 4, marginBottom: 14 }}>You can’t add a paying client until payments are connected — a couple of quick steps:</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {setupSteps.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, background: "#fff", border: "1px solid #F0E2BC", borderRadius: 12, padding: "11px 14px" }}>
                <span style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, background: s.done ? "#1A8A52" : "#F6D88A", color: s.done ? "#fff" : "#8A5A06" }}>{s.done ? "✓" : i + 1}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#1D1D1F", textDecoration: s.done ? "line-through" : "none", opacity: s.done ? .55 : 1 }}>{s.label}</div>
                  <div style={{ fontSize: 12, color: "#86868B" }}>{s.desc}</div>
                </div>
                {!s.done && <button onClick={s.go} style={{ border: "none", cursor: "pointer", font: "inherit", fontSize: isMobile ? 14 : 13, fontWeight: 600, padding: isMobile ? "10px 14px" : "8px 14px", minHeight: isMobile ? 40 : undefined, borderRadius: 10, background: "#0A84FF", color: "#fff", flexShrink: 0 }}>Set up →</button>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── HERO — cinematic: aurora mesh + signature completion ring + frosted money strip ── */}
      <div style={{
        position: "relative", overflow: "hidden", borderRadius: isMobile ? 20 : 28, color: "#fff",
        background: "linear-gradient(135deg, #0A1B4A 0%, #0B3DA8 50%, #0A84FF 100%)",
        boxShadow: "0 34px 80px -24px rgba(10,70,190,.6)", padding: isMobile ? "22px 18px 20px" : "34px 40px 30px",
      }}>
        {/* aurora — soft blurred color blobs for depth.
            On phones the DOCUMENT scrolls, so every scroll repaints this hero; three
            blur() layers + a backdrop-filter are the landing page's main jank source,
            and WebKit doesn't clip filtered (composited) children to the rounded
            corners either. On mobile the blobs keep their radial falloff but drop the
            filter, so they clip cleanly and paint cheaply. */}
        <div style={{ position: "absolute", top: -120, left: -60, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(48,209,255,.5), transparent 65%)", filter: isMobile ? "none" : "blur(24px)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: -90, right: 140, width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(120,99,255,.5), transparent 65%)", filter: isMobile ? "none" : "blur(24px)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -150, right: -50, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(10,132,255,.55), transparent 65%)", filter: isMobile ? "none" : "blur(14px)", pointerEvents: "none" }} />

        {/* top row — today + completion ring.
            Phone: the ring stays BESIDE the headline (nowrap, text flex:1) at 96px so it
            never drops to an orphaned row, and the CTA moves below as a full-width bar. */}
        <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center", gap: isMobile ? 16 : 32, flexWrap: isMobile ? "nowrap" : "wrap" }}>
          <div style={{ minWidth: isMobile ? 0 : 240, flex: isMobile ? 1 : undefined }}>
            <div style={{ fontSize: 13.5, fontWeight: 500, color: "rgba(255,255,255,.7)" }}>{greeting} · {dateStr}</div>
            <div style={{ fontSize: isMobile ? 30 : 44, fontWeight: 600, letterSpacing: "-.035em", lineHeight: 1.04, marginTop: 10, overflowWrap: "anywhere" }}>
              {total === 0 ? "No jobs today" : <>{total} job{total > 1 ? "s" : ""} <span style={{ color: "rgba(255,255,255,.58)" }}>on today’s route</span></>}
            </div>
            {!isMobile && (
              <button onClick={() => setPage("route")} style={{
                marginTop: 20, border: "none", cursor: "pointer", font: "inherit", fontSize: 14, fontWeight: 600,
                padding: "11px 20px", borderRadius: 12, background: "#fff", color: "#0B4DC0", boxShadow: "0 10px 24px rgba(0,0,0,.22)",
              }}>View today’s route →</button>
            )}
          </div>

          {/* signature — circular completion ring */}
          <div style={{ position: "relative", flexShrink: 0, width: ringSize, height: ringSize }}>
            <svg width={ringSize} height={ringSize} viewBox={`0 0 ${ringSize} ${ringSize}`}>
              <circle cx={ringSize / 2} cy={ringSize / 2} r={ringR} fill="none" stroke="rgba(255,255,255,.18)" strokeWidth={ringStroke} />
              <circle cx={ringSize / 2} cy={ringSize / 2} r={ringR} fill="none" stroke="#fff" strokeWidth={ringStroke} strokeLinecap="round"
                strokeDasharray={ringC} strokeDashoffset={ringC * (1 - (total ? pct : 0) / 100)}
                transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`} style={{ transition: "stroke-dashoffset .7s ease" }} />
            </svg>
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <div style={{ fontSize: isMobile ? 24 : 30, fontWeight: 600, letterSpacing: "-.03em", lineHeight: 1 }}>{total ? pct : 0}%</div>
              <div style={{ fontSize: isMobile ? 10 : 11, color: "rgba(255,255,255,.7)", marginTop: 2 }}>{total ? `${doneCount}/${total} done` : "no route"}</div>
            </div>
          </div>
        </div>

        {isMobile && (
          <button onClick={() => setPage("route")} style={{
            position: "relative", marginTop: 18, width: "100%", minHeight: 46, border: "none", cursor: "pointer", font: "inherit", fontSize: 15, fontWeight: 600,
            padding: "12px 20px", borderRadius: 12, background: "#fff", color: "#0B4DC0", boxShadow: "0 10px 24px rgba(0,0,0,.22)",
          }}>View today’s route →</button>
        )}

        {/* frosted-glass money strip floating on the gradient.
            Phone: no backdrop-filter (flat tint reads the same on the gradient and
            stops the per-scroll repaint); MRR takes the full first row and
            Forecast / Avg sit as two equal columns on the second — no ragged wrap. */}
        <div style={{
          position: "relative", marginTop: isMobile ? 16 : 26, display: "flex", flexWrap: "wrap", gap: isMobile ? 12 : 18, alignItems: "center",
          background: isMobile ? "rgba(255,255,255,.15)" : "rgba(255,255,255,.12)",
          backdropFilter: isMobile ? undefined : "blur(18px)", WebkitBackdropFilter: isMobile ? undefined : "blur(18px)",
          border: "1px solid rgba(255,255,255,.2)", borderRadius: 18, padding: isMobile ? "14px 16px" : "16px 22px",
        }}>
          <div style={{ flex: isMobile ? "1 1 100%" : "1 1 170px", minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase", color: "rgba(255,255,255,.62)" }}>Monthly recurring</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
              <span style={{ fontSize: 30, fontWeight: 600, letterSpacing: "-.03em", whiteSpace: "nowrap" }}>{money2(mrr)}</span>
              {momPct !== null && <span style={{ fontSize: 13, fontWeight: 600, color: momDelta >= 0 ? "#7DF0B0" : "#FFB4AE" }}>{momDelta >= 0 ? "▲" : "▼"} {Math.abs(momPct).toFixed(0)}%</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: isMobile ? 16 : 26, alignItems: "center", flexWrap: isMobile ? "nowrap" : "wrap", width: isMobile ? "100%" : undefined }}>
            <div style={{ flex: isMobile ? 1 : undefined, minWidth: 0 }}><div style={{ fontSize: 11.5, color: "rgba(255,255,255,.66)" }}>Forecast</div><div style={{ fontSize: 16, fontWeight: 600, marginTop: 2, whiteSpace: "nowrap" }}>{money2(nextMonthForecast)}</div></div>
            <div style={{ flex: isMobile ? 1 : undefined, minWidth: 0 }}><div style={{ fontSize: 11.5, color: "rgba(255,255,255,.66)" }}>Avg/client</div><div style={{ fontSize: 16, fontWeight: 600, marginTop: 2, whiteSpace: "nowrap" }}>{money2(avgRevenuePerClient)}</div></div>
          </div>
        </div>
      </div>

      {/* ── Needs attention ── */}
      <div className="card">
        <div style={label}>Needs attention</div>
        {attention.length === 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, color: "var(--ok)" }}>
            <span style={{ fontSize: 18 }}>✓</span>
            <span style={{ fontSize: 14, fontWeight: 500 }}>You’re all caught up — nothing needs you right now.</span>
          </div>
        ) : (
          <div style={{ marginTop: 8 }}>
            {attention.map((a, i) => (
              <div key={i} onClick={a.go}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", minHeight: isMobile ? 48 : undefined, borderBottom: i < attention.length - 1 ? "1px solid var(--border)" : "none", cursor: "pointer" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: a.dot, flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: "var(--text)", flex: 1 }}>{a.text}</span>
                <span style={{ color: "var(--text3)", fontSize: 16 }}>›</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Revenue trend (promoted, full width) ── */}
      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Revenue — last 6 months</div>
          <span style={sub}>6-mo total <strong style={{ color: "var(--text)" }}>{money(sixMoTotal)}</strong></span>
        </div>
        <div className="chart-bar-wrap" style={{ height: 120 }}>
          {revData.map((val, i) => {
            const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
            return (
              <div key={i} className="chart-bar-col">
                <div className="chart-bar">
                  <div className="chart-bar-fill" style={{ height: `${(val / maxRev) * 100}%`, background: i === 5 ? "var(--accent)" : "rgba(0,0,0,.18)" }} />
                </div>
                <div className="chart-label">{d.toLocaleDateString("en-US", { month: "short" })}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Today's jobs + Top clients ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18 }}>
        <div className="card">
          <div className="card-title">Today’s jobs</div>
          {total === 0
            ? <div className="empty"><p>No jobs today 🎉</p></div>
            : todayJobs.map((j, i) => (
              /* Phone: the whole row is a <label>, so tapping the client name toggles the
                 job — the 17px box alone is far below the 44px touch minimum. */
              <JobRow key={j.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 0", minHeight: isMobile ? 48 : undefined, borderBottom: i < total - 1 ? "1px solid var(--border)" : "none" }}>
                <input type="checkbox" checked={!!jobsDone[j.id]} onChange={() => toggleJob(j.id)} style={{ accentColor: "var(--accent)", width: isMobile ? 22 : 17, height: isMobile ? 22 : 17, flexShrink: 0, margin: isMobile ? 0 : undefined }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5, textDecoration: jobsDone[j.id] ? "line-through" : "none", color: jobsDone[j.id] ? "var(--text3)" : "var(--text)" }}>{j.client_name || j.clientName}</div>
                  <div style={{ fontSize: 12, color: "var(--text2)" }}>{j.client_address || j.address} · {j.dogs} dog{j.dogs > 1 ? "s" : ""}</div>
                </div>
              </JobRow>
            ))}
        </div>

        <div className="card">
          <div className="card-title">Top clients by revenue</div>
          {topClients.length === 0
            ? <div className="empty"><p>No active clients yet</p></div>
            : topClients.map((c, i) => (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 0", borderBottom: i < topClients.length - 1 ? "1px solid var(--border)" : "none" }}>
                <div style={{ width: 24, height: 24, borderRadius: "50%", background: "var(--accent-weak)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, flexShrink: 0 }}>{i + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
                  <div style={{ fontSize: 11.5, color: "var(--text2)", textTransform: "capitalize" }}>{c.frequency} · {c.dogs} dog{c.dogs > 1 ? "s" : ""}</div>
                </div>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{money2(clientMonthly(c, pricing))}</div>
              </div>
            ))}
        </div>
      </div>

      {/* ── Secondary metrics (demoted, quiet strip) ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 18 }}>
        {[
          { k: "Active clients", v: active, s: `${clients.length} total` },
          { k: "Invoices paid", v: paid, s: "all time" },
          { k: "Pending", v: money2(pendingAmt), s: "to collect" },
          { k: "Churned (mo)", v: deactivatedThisMonth, s: "this month" },
        ].map((m) => (
          <div key={m.k} style={{ padding: "4px 2px" }}>
            <div style={{ ...sub, fontSize: 12 }}>{m.k}</div>
            <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-.02em", marginTop: 4 }}>{m.v}</div>
            <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 1 }}>{m.s}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
